import React, {useMemo} from "react";
import {Message} from "../ai.types";
import SummariseToIncidentClientSingleton from "../clients/ai.createIncident.client";
import AzureAiClientBase, {defaultAzureAiConfig, TAzureAiClientConstructor} from "../clients/ai.base.client";
import {useDebug} from "@me8eon/debug";
import {AiRagClient, aiRagDebug, useAiSourceMemory} from "../clients/ai.rag.client";
import {EnvironmentConfig} from "@me8eon/config";
import {QuickActionType} from "../../../modules/AiWidgets/widget.types";
import {useJwtToken, useUserData} from "@me8eon/react_authentication";
import {useChatbotIndicesHook} from "../hooks/useChatbotIndices.hook";
import ValidateIntent from "../clients/ai.validateIntent.client";
import { FeatureFlags } from "@me8eon/featureflags/src/feature.flags";
import {useFeatureFlag} from "@me8eon/featureflags";
import { MessageWithActionAndData } from "@me8eon/sessions_messages";

export type OutputAndSource = {
    output: string;
    source: string;
    action?: string;
    data?: any;
};

export type HandleRagQuery = (history: Message[], query: string, language: string, featureFlagEnabled?: boolean, action?: string, ff?: FeatureFlags) => Promise<OutputAndSource>
export type HandleQuickActions = (history: MessageWithActionAndData[], query: string, quickActionType: QuickActionType, data: any) => any
export type HandleSummariseChatHistoryToIncident = (history: Message[], reason: string, language: string) => Promise<OutputAndSource>
export type HandleValidateIntent = (query: string) => Promise<OutputAndSource>

export type AiClient = {
    baseAiClient: AzureAiClientBase,
    handleRagQuery: HandleRagQuery,
    handleQuickActions: HandleQuickActions,
    handleSummariseChatHistoryToIncident: HandleSummariseChatHistoryToIncident
    handleValidateIntent: HandleValidateIntent
} & ReturnType<typeof useChatbotIndicesHook>;

const AiRagContext = React.createContext<AiClient | undefined>(
    undefined,
);

export function AiRagProvider({children}: { children: React.ReactNode }) {
    const chatbotIndices = useChatbotIndicesHook();
    const jwtTokenFn = useJwtToken();
    const esTokenFn = useJwtToken();
    const { companyId } = useUserData();

    const searchEmbeddingModel = useMemo(() => {
        return ".multilingual-e5-small_linux-x86_64";
    }, []);


    const aiConfig: TAzureAiClientConstructor = {
        config: defaultAzureAiConfig(EnvironmentConfig.nodeService),
        jwtTokenFn,
    };

    const rememberSearchOps = useAiSourceMemory();
    const debug = useDebug(aiRagDebug);

    const baseAiClient = new AzureAiClientBase(aiConfig);
    const aiSummariseClient = new SummariseToIncidentClientSingleton(aiConfig);
    const aiRagClient = new AiRagClient({
        config: aiConfig,
        rememberSearchOps,
        searchEmbeddingModel,
        debug,
        companyId
    });
    const aiValidateIntentClient = new ValidateIntent(aiConfig)

    const handleSummariseChatHistoryToIncident: HandleSummariseChatHistoryToIncident = async (history, reason, language,) =>
        aiSummariseClient.summariseToIncident(history, reason, language);
    const handleRagQuery: HandleRagQuery = async (history, query, language, featureFlagEnabled, action, ff) =>
        aiRagClient.handleQuery(history, query, language, esTokenFn, chatbotIndices.activeSearchIndices, chatbotIndices.activePersonalizedIndices, featureFlagEnabled, ff);
    const handleQuickActions: HandleQuickActions = async (history, query, quickActionType, data) =>
        aiRagClient.aiClientQuickActions(history, query, quickActionType, data);

    const handleValidateIntent = async (query: string) => await aiValidateIntentClient.handleValidateIntent(query);
    
    return (
        <AiRagContext.Provider value={{
            baseAiClient,
            handleSummariseChatHistoryToIncident,
            handleRagQuery,
            handleQuickActions,
            handleValidateIntent,
            ...chatbotIndices
        }}>
            {children}
        </AiRagContext.Provider>
    );
}

export function useAiWithRag(): AiClient {
    const client = React.useContext(AiRagContext);
    if (!client)
        throw new Error("useAiWithRag must be used within a AiRagProvider");
    return client;
}
